import { GzDialogWebComponent } from '../../ui/gzDialog';
import { MobileSecurityActivationEmailsDialogBodyWebComponent } from './mobileSecurityActivationEmailsDialogBody';
import { translateFn } from '../../utils/translator.helper';
import { GzButtonWebComponent } from '../../ui/gzButton';
import { showErrorToast, showSuccessToast } from '@nirvana/shared/common';
import { WebComponentEvent } from '../../framework/BaseWebComponentController';
import { GzToastNotificationWebComponent } from '../../ui/gzToastNotification';
import { ToastActionButtonMode, ToastSeverity } from '../../ui/gzToastNotification/type';
import {
  EMAILS_CHANGE_EVENT,
  USER_ACTIVITY_ACTION_FAILED_ACTIVATION_EMAILS,
  USER_ACTIVITY_MODEL_MOBILE_SECURITY,
} from '../common/constants';

interface SendActivationEmailsResponse {
  success: boolean;
  rateLimitError?: boolean | null;
  failedEmailsCount?: number | null;
}

export class MobileSecurityActivationEmailsDialog {
  private readonly title: string;
  private readonly body: MobileSecurityActivationEmailsDialogBodyWebComponent;
  private readonly footerHtml: string;
  private readonly company: { id: string; name: string };
  private readonly dependencies: any;
  private dialog: GzDialogWebComponent;

  constructor(
    dialogBody: MobileSecurityActivationEmailsDialogBodyWebComponent,
    company: { id: string; name: string },
    dependencies: any
  ) {
    this.title = translateFn('Activation emails');
    this.body = dialogBody;
    this.footerHtml = this.getFooterHtml();
    this.company = company;
    this.dependencies = dependencies;
  }

  private static setButtonLoading(button: GzButtonWebComponent | null, isLoading: boolean) {
    if (button) {
      button.showSpinner = isLoading;
      button.disabled = isLoading;
    }
  }

  public getFooterHtml() {
    return `
      <gz-button id="confirm" type="full" color="primary" class="mr-2"> ${translateFn('Send')} </gz-button>
      <gz-button id="cancel" type="outline" color="secondary"> ${translateFn('Cancel')} </gz-button>
    `;
  }

  public show() {
    this.dialog = GzDialogWebComponent.computeCustomDialog(
      this.body,
      this.footerHtml,
      this.title,
      null,
      this.confirmCallback.bind(this),
      { dependencies: this.dependencies },
      false
    );

    this.setValidatorForSendButton();
  }

  private setValidatorForSendButton() {
    const sendButton = this.dialog.footerContent?.querySelector<GzButtonWebComponent>('#confirm') ?? null;

    if (!sendButton) {
      return;
    }

    sendButton.disabled = true;

    this.body.addEventListener(EMAILS_CHANGE_EVENT, (event: WebComponentEvent) => {
      sendButton.disabled = event.detail.emails.length === 0;
    });
  }

  private async confirmCallback() {
    const sendButton = this.dialog.shadowRoot?.querySelector<GzButtonWebComponent>('#confirm') ?? null;

    MobileSecurityActivationEmailsDialog.setButtonLoading(sendButton, true);

    const response = await this.trySendActivationEmails();

    this.displayToastNotification(response);

    MobileSecurityActivationEmailsDialog.setButtonLoading(sendButton, false);
    this.dialog.close();
  }

  private async trySendActivationEmails(): Promise<SendActivationEmailsResponse> {
    let response: SendActivationEmailsResponse;

    try {
      const companyId = this.company.id;
      const emailAddresses = this.body.emails;

      response = await this.dependencies.api.clients.MobileSecurity.sendActivationEmails(
        companyId,
        emailAddresses
      ).toPromise();

      if (!response || response.success === undefined) {
        return { success: false, failedEmailsCount: null };
      }
    } catch (error) {
      return { success: false, failedEmailsCount: null };
    }

    return response;
  }

  private displayToastNotification(response: SendActivationEmailsResponse) {
    if (response.success) {
      showSuccessToast(translateFn('Activation emails have been sent successfully.'));
      return;
    }

    let errorMessage;

    if (response.rateLimitError === true){
      errorMessage = translateFn('You have reached the activation email limit. Try again in one minute.');
      showErrorToast(new Error(errorMessage));
      return;
    }

    if (typeof response.failedEmailsCount !== 'number') {
      errorMessage = translateFn('Unexpected error occurred while sending the activation emails.');
      showErrorToast(new Error(errorMessage));
      return;
    }

    this.displayErrorToastNotificationWithRedirect(response.failedEmailsCount);
  }

  private displayErrorToastNotificationWithRedirect(failedEmailsCount: number) {
    const isTotalFailure = failedEmailsCount === this.body.emails.length;

    const errorMessage = isTotalFailure
      ? translateFn('All activation emails failed to send. For more details check')
      : translateFn('Some activation emails failed to send. For more details check');

    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: errorMessage,
      severity: isTotalFailure ? ToastSeverity.ERROR : ToastSeverity.WARNING,
      showInlineButton: true,
      hasAction: true,
      actionButtonText: translateFn('User Activity'),
      actionButtonMode: ToastActionButtonMode.LINK,
      actionCallback: () => {
        const params: any = {
          companyId: this.company.id,
          model: USER_ACTIVITY_MODEL_MOBILE_SECURITY
        }

        if (isTotalFailure) {
          params.action = USER_ACTIVITY_ACTION_FAILED_ACTIVATION_EMAILS
        }

        Nav.to(`/user-activity`, params);
      },
    });
  }
}
